'use client';

import { Phone } from 'lucide-react';
import { EditableContent } from '../../edit/EditableContent';
import { LucideIcon } from '../../icons/LucideIcon';
import type { BlockRenderProps } from '../../types';
import { ContactMessage, ContactPicture } from './contactChrome';
import { readContactView } from './contactView';

/**
 * Visit: the photo is the section. Address is a caption, phone is a call,
 * hours are one open line. The footer keeps the full directory.
 * Section id is `visit` so it does not steal the footer `#contact` anchor.
 */
export function ContactBlock({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const view = readContactView({ block, locale, isActive, activeEditTarget, editable });
  const { target } = view;

  return (
    <section
      className={['ws-contact', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-contact-arrangement="visit"
      id="visit"
    >
      <div className="ws-contact__inner">
        <header className="ws-contact__header">
          <div className="ws-contact__title-row">
            <EditableContent
              as="span"
              fieldKey="sectionIcon"
              editable={editable}
              activeEditTarget={target}
              className="ws-contact__icon-hit"
            >
              <LucideIcon icon={view.sectionIcon} size="lg" className="ws-contact__icon" />
            </EditableContent>
            <EditableContent
              as="h2"
              fieldKey="sectionTitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-contact__title"
            >
              {view.sectionTitle}
            </EditableContent>
          </div>
          {view.intro || editable ? (
            <EditableContent
              fieldKey="intro"
              editable={editable}
              activeEditTarget={target}
              className="ws-contact__intro"
            >
              {view.intro}
            </EditableContent>
          ) : null}
        </header>

        {view.showMap ? (
          <ContactPicture
            view={view}
            className="ws-contact__visual"
            photoClass="ws-contact__photo"
          />
        ) : null}

        {view.address || editable ? (
          <EditableContent
            fieldKey="address"
            editable={editable}
            activeEditTarget={target}
            className="ws-contact__caption"
          >
            {view.address}
          </EditableContent>
        ) : null}

        <div className="ws-contact__actions">
          {view.phone || editable ? (
            view.telHref && !editable ? (
              <a className="ws-contact__call" href={view.telHref}>
                <LucideIcon icon={Phone} size="sm" className="ws-contact__action-icon" />
                <span>{view.phone}</span>
              </a>
            ) : (
              <EditableContent
                as="span"
                fieldKey="phone"
                editable={editable}
                activeEditTarget={target}
                className="ws-contact__call"
              >
                {view.phone}
              </EditableContent>
            )
          ) : null}
          {view.hoursLine || editable ? (
            <EditableContent
              fieldKey="hours"
              editable={editable}
              activeEditTarget={target}
              className="ws-contact__open"
            >
              {view.hoursLine}
            </EditableContent>
          ) : null}
        </div>

        <ContactMessage
          view={view}
          locale={locale}
          className="ws-contact__form-panel"
          titleClass="ws-contact__form-title"
          supportClass="ws-contact__form-support"
        />
      </div>
    </section>
  );
}
